import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Minimize2 } from "lucide-react";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useCallback, useRef, useState } from "react";

import {
  type AdverseEventLevel,
  type AdverseEventType,
  apiPostEvents,
  type DeathEventReason,
  EventCategory,
  type HospitalizationEventReason,
  type HospitalizationEventType,
  type LabsEventType,
  type ListEventCategories,
  PatientStatus,
  type PhysicianNotifiedEventType,
  type PostApiV1EventsData,
  type VisitEventPlannedCategory,
  type VisitEventType,
  type VisitEventUnplannedCategory,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { Button } from "../../../components/ui-kit/button";
import { Dialog } from "../../../components/ui-kit/dialog";
import { useDialog } from "../../../components/ui-kit/dialog-provider";
import { DateInput } from "../../../components/ui-kit/fields/date-input";
import { Select } from "../../../components/ui-kit/fields/select";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import { LazyRichTextEditor } from "../../../components/ui-kit/lazy-rich-text-editor";
import { hasRichText } from "../../../components/ui-kit/sanitize-rich-text";
import { ShortcutHint } from "../../../components/ui-kit/shortcut-hint";
import {
  RowAccordion,
  useRowAccordion,
} from "../../../components/ui-kit/tables/row-accordion";
import { getServerTodayISO } from "../../../utils/server-time";
import { createRowId, dateTimeISOString } from "../../../utils/utils";
import {
  eventCategory,
  EventCategoryDetails,
  EventCategoryType,
  eventDetailsEnum,
  eventTypesEnum,
} from "./enums";

/** One source for the hint, the tooltip and the ARIA attribute, so they can't drift. */
export const hideShortcut = { key: "e", hint: "⌘E", aria: "Meta+E Control+E" };

export const AddEventsDialog = observer(function AddEventsDialog({
  app,
  eventOptions,
  draft: accordion,
  onHide,
  onClose,
  onSaved,
}: {
  app: AppModel;
  eventOptions: ListEventCategories | null;
  /** Owned by the caller so the rows survive while the dialog is hidden. */
  draft: EventsDraft;
  /** Keeps the rows for the caller to re-open; the count badges its button. */
  onHide: (startedRowCount: number) => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLingui();
  const patient = app.requirePatient();
  const api = app.seerlinqApi;
  const { confirm } = useDialog();

  const scrollRef = accordion.scroll;

  // Re-opening should land where the user left off. A ref callback rather than a
  // mount effect: the content sits in a portal that attaches a commit later, so
  // an effect would run before there is an element.
  const trackScroll = useCallback(
    (el: HTMLDivElement) => {
      // Captured once so the cleanup writes to the draft this mount belonged to;
      // `reset` swaps in a fresh box before React unmounts us.
      const scroll = scrollRef.current;
      const target = scroll.top;
      let frame = 0;
      if (target > 0) {
        el.scrollTop = target;
        // A scrollTop past the current height is clamped, and the rich-text
        // editors grow the content after this paint — so re-apply from `target`,
        // not the ref, which the clamped assignment already overwrote.
        frame = requestAnimationFrame(() => {
          el.scrollTop = target;
        });
      }

      const remember = () => {
        scroll.top = el.scrollTop;
      };
      el.addEventListener("scroll", remember, { passive: true });

      return () => {
        cancelAnimationFrame(frame);
        // Take a final reading while the node is still attached: a scroll in the
        // same frame as the unmount may not have fired yet.
        if (el.isConnected) {
          remember();
        }
        el.removeEventListener("scroll", remember);
      };
    },
    [scrollRef],
  );

  const hasValidRows =
    accordion.rows.length > 0 &&
    accordion.rows.every((r) => isEventRowComplete(r, eventOptions));

  /**
   * Escape and clicking outside hide rather than discard: losing a half-typed
   * event to a stray click is the worse outcome. With nothing typed there is
   * nothing to keep, so they just close.
   */
  function dismiss() {
    const started = accordion.startedRowCount();
    if (started > 0) {
      onHide(started);
    } else {
      onClose();
    }
  }

  /** Discarding is only reachable from Cancel, so it asks first. */
  async function cancel() {
    const started = accordion.startedRowCount();
    if (
      started > 0 &&
      !(await confirm(
        plural(started, {
          one: "It won't be added to the patient's record.",
          other: "They won't be added to the patient's record.",
        }),
        plural(started, {
          one: "Discard # unsaved event?",
          other: "Discard # unsaved events?",
        }),
        { confirmLabel: t`Discard`, cancelLabel: t`Keep editing` },
      ))
    ) {
      return;
    }
    onClose();
  }

  async function save() {
    const items = accordion.rows.map((r) => ({
      rowId: r.__rowId,
      payload: toEventPayload(r, patient.patientId),
    }));

    let succeededCount = 0;
    let firstError: unknown = null;
    let deathCreated = false;
    for (const { rowId, payload } of items) {
      try {
        await apiPostEvents({ body: payload });
        accordion.removeRow(rowId);
        succeededCount += 1;
        if (payload.event_category === EventCategory.death) {
          deathCreated = true;
        }
      } catch (error) {
        // Keep going so one bad row doesn't block the valid rows after it; the
        // failed rows stay in the dialog for the user to fix and retry.
        firstError ??= error;
      }
    }

    if (succeededCount > 0) {
      api.clearResponseCache();
      onSaved();
    }

    if (
      deathCreated &&
      patient.data?.patient_status !== PatientStatus.deceased
    ) {
      // A Death event means the patient has died, so reflect that in their status.
      try {
        await patient.updatePatientField({
          patient_status: PatientStatus.deceased,
        });
      } catch (error) {
        firstError ??= error;
      }
    }

    if (firstError != null) {
      // Surface a single dialog (the service shows one at a time) rather than
      // one per failed row; the rows left behind show what still needs fixing.
      window.handleError(firstError);
    } else {
      onClose();
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && dismiss()}
      className="top-[5dvh] max-h-[90dvh] w-[750px] translate-y-0"
    >
      <div className="mb-6 flex items-start justify-between gap-4">
        <h3 className="text-base font-bold">
          <Trans>Add events</Trans>
        </h3>
        {/* Lets the user check the events already on record without losing the
            rows typed so far. */}
        <Button
          type="button"
          variant="subtle"
          size="sm"
          // Always keeps the rows, even untouched ones — unlike `dismiss`, this
          // is an explicit "show me the table" rather than a stray gesture.
          onClick={() => onHide(accordion.startedRowCount())}
          aria-keyshortcuts={hideShortcut.aria}
          title={`${t`Hide this dialog to see the events table; your rows are kept`} (${hideShortcut.hint})`}
        >
          <Minimize2 size={14} />
          <Trans>Hide</Trans>
          <ShortcutHint>{hideShortcut.hint}</ShortcutHint>
        </Button>
      </div>

      <Form className="flex min-h-0 flex-1 flex-col" onSubmit={() => save()}>
        <div
          ref={trackScroll}
          data-slot="add-events-scroll"
          className="min-h-0 overflow-y-auto overflow-x-hidden py-1 px-1"
        >
          <RowAccordion
            {...accordion}
            summaryLabel={(row, index) =>
              row.event_category
                ? resolveEnumLabel(eventCategory[row.event_category]) ||
                  row.event_category
                : t`Event ${index + 1}`
            }
            renderRow={(row) => (
              <RowForm row={row} eventOptions={eventOptions} />
            )}
          />
        </div>

        <div className="mt-6 flex justify-center gap-2">
          <Button type="submit" disabled={!hasValidRows}>
            <Trans>Add</Trans>
          </Button>
          <Button
            type="button"
            variant="subtle"
            size="default"
            // Not handed the promise: Button would spin for as long as the
            // confirmation is up, and nothing is loading.
            onClick={() => void cancel()}
          >
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </Form>
    </Dialog>
  );
});

/**
 * Draft rows of the add-events dialog, held by the caller so hiding the dialog
 * (which unmounts it) doesn't throw away what the user has typed.
 */
export function useEventsDraft() {
  // Lazily, so the blank starting row isn't rebuilt on every render.
  const [initialRows] = useState(() => [createEventRow()]);
  const accordion = useRowAccordion(createEventRow, initialRows);
  /**
   * How far the content was scrolled, so re-opening resumes there. Boxed so
   * `reset` can hand out a new box: the dialog unmounts *after* the reset and
   * writes one last reading on its way out, which would undo the zeroing here.
   */
  const scroll = useRef({ top: 0 });
  return {
    ...accordion,
    scroll,
    /** Back to a single blank row — call once a draft is saved or dropped. */
    reset: () => {
      scroll.current = { top: 0 };
      accordion.initRows([createEventRow()]);
    },
    /** Rows the user has actually started, for the "Add events" badge. */
    startedRowCount: () => accordion.rows.filter(isEventRowStarted).length,
  };
}

export type EventsDraft = ReturnType<typeof useEventsDraft>;

const RowForm = observer(function RowForm({
  row,
  eventOptions,
}: {
  row: EventRow;
  eventOptions: ListEventCategories | null;
}) {
  const { t } = useLingui();
  const typeOptions = categoryTypeOptions(row, eventOptions);
  const detailOptions = categoryDetailOptions(row, eventOptions);

  return (
    <FormTable dataAdd className="max-w-full">
      <FormRow
        label={t`Date`}
        data={<DateInput model={row} attr="event_date" type="date" />}
        mandatory
      />
      <FormRow
        label={t`Category`}
        data={
          <Select
            model={row}
            attr="event_category"
            values={eventCategory}
            onChange={() => {
              row.event_category_type = null;
              row.event_category_details = null;
            }}
          />
        }
        mandatory
      />
      <FormRow
        label={t`Category Type`}
        data={
          <Select
            model={row}
            attr="event_category_type"
            values={eventTypesEnum(typeOptions)}
            disabled={typeOptions.length === 0}
            onChange={() => {
              row.event_category_details = null;
            }}
          />
        }
        mandatory={typeOptions.length > 0}
      />
      <FormRow
        label={t`Category Details`}
        data={
          <Select
            model={row}
            attr="event_category_details"
            values={eventDetailsEnum(detailOptions)}
            disabled={detailOptions.length === 0}
          />
        }
        mandatory={detailOptions.length > 0}
      />
      <FormRow
        label={t`Description`}
        data={
          <LazyRichTextEditor
            className="w-[330px]"
            toolbar={["text-formatting"]}
            value={row.event_description ?? ""}
            onChange={(v) => {
              row.event_description = v;
            }}
          />
        }
        mandatory
      />
      <FormRow
        label={t`Comment`}
        data={
          <LazyRichTextEditor
            className="w-[330px]"
            toolbar={["text-formatting"]}
            value={row.event_comment ?? ""}
            onChange={(v) => {
              row.event_comment = v;
            }}
          />
        }
      />
    </FormTable>
  );
});

type EventCategoryTypeValue =
  | HospitalizationEventType
  | DeathEventReason
  | VisitEventType
  | LabsEventType
  | PhysicianNotifiedEventType
  | AdverseEventType;

type EventCategoryDetailsValue =
  | HospitalizationEventReason
  | VisitEventUnplannedCategory
  | VisitEventPlannedCategory
  | AdverseEventLevel;

type EventRow = {
  __rowId: string;
  /** The pre-filled date this row was created with — see isEventRowStarted. */
  __initialDate: string;
  event_date: string;
  event_category: EventCategory | null;
  event_category_type: EventCategoryTypeValue | null;
  event_category_details: EventCategoryDetailsValue | null;
  event_description: string | null;
  event_comment: string | null;
};

// ListEventCategories has per-category keys, not a string index signature.
type EventOptionsMap = Record<string, Record<string, string[]> | null> | null;

function createEventRow(): EventRow {
  const today = getServerTodayISO();
  return observable({
    __rowId: createRowId(),
    __initialDate: today,
    event_date: today,
    event_category: null,
    event_category_type: null,
    event_category_details: null,
    event_description: null,
    event_comment: null,
  });
}

function categoryTypeOptions(
  row: EventRow,
  eventOptions: ListEventCategories | null,
): EventCategoryType[] {
  const opts = eventOptions as EventOptionsMap;
  return row.event_category
    ? (Object.keys(opts?.[row.event_category] ?? {}) as EventCategoryType[])
    : [];
}

function categoryDetailOptions(
  row: EventRow,
  eventOptions: ListEventCategories | null,
): EventCategoryDetails[] {
  const opts = eventOptions as EventOptionsMap;
  return row.event_category && row.event_category_type
    ? ((opts?.[row.event_category]?.[row.event_category_type] ??
        []) as EventCategoryDetails[])
    : [];
}

/**
 * Whether anything has been entered into a row, so an untouched blank row leaves
 * the "Add events" button unbadged. The date is pre-filled, so it counts only
 * once changed — compared against the row's own initial value rather than today,
 * so a dialog left open across midnight doesn't turn untouched rows "started".
 */
function isEventRowStarted(row: EventRow): boolean {
  return Boolean(
    row.event_category ||
    row.event_category_type ||
    row.event_category_details ||
    hasRichText(row.event_description) ||
    hasRichText(row.event_comment) ||
    row.event_date !== row.__initialDate,
  );
}

// Single source of truth for the mandatory-field contract: a category type or
// details value is required only when the catalog offers options for it.
function isEventRowComplete(
  row: EventRow,
  eventOptions: ListEventCategories | null,
): boolean {
  if (
    !row.event_date ||
    !row.event_category ||
    !hasRichText(row.event_description)
  ) {
    return false;
  }
  if (
    categoryTypeOptions(row, eventOptions).length > 0 &&
    !row.event_category_type
  ) {
    return false;
  }
  return !(
    categoryDetailOptions(row, eventOptions).length > 0 &&
    !row.event_category_details
  );
}

function toEventPayload(
  row: EventRow,
  patientId: number,
): PostApiV1EventsData["body"] {
  // The body is a discriminated union keyed by event_category, where each member
  // also fixes which type/details values it allows. This dialog is category-
  // agnostic (the category is only known at runtime), so the payload can't be
  // narrowed to a single member statically. isEventRowComplete has already
  // validated the row, so cast the assembled object to the body type.
  return {
    event_date: row.event_date,
    event_timestamp: dateTimeISOString(),
    event_category: row.event_category,
    event_category_type: row.event_category_type,
    event_category_details: row.event_category_details,
    event_description: row.event_description,
    event_comment: row.event_comment,
    patient_id: patientId,
  } as PostApiV1EventsData["body"];
}
